import { useState, type ReactNode } from 'react'
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Breadcrumbs from '@mui/material/Breadcrumbs'
import Button from '@mui/material/Button'
import ButtonBase from '@mui/material/ButtonBase'
import Checkbox from '@mui/material/Checkbox'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControlLabel from '@mui/material/FormControlLabel'
import MuiLink from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Typography from '@mui/material/Typography'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import type { Document, Manifest, ManifestIntent, ManifestReference, Reference } from '../../api/authoring'
import { useGetAuthoringComponentQuery, useGetManifestQuery, useListUsagesQuery } from '../../api/authoring'
import { problemMessage } from '../../api/baseApi'
import { payloadId, useIntent, shown } from '../../api/useIntent'
import { editorFor } from '../../editors'
import { IntentForm } from '../../editors/IntentForm'
import { aOrAn, humanise } from '../../model/labels'
import { formableIntents, inFieldOrder, proseOf, typeOf, typeProse } from '../../model/manifest'
import { nameOrId } from '../../model/name'
import { declarationOf } from '../../model/pattern'
import {
  ADDRESS_SECTION,
  fileTarget,
  heldUnder,
  intentSection,
  keyAndLabel,
  libraryChoices,
  members,
  ownedChoices,
  ownedReferences,
  recordAddress,
  recordPath,
  sectionOrder,
  settingsFields,
  type RecordAddress,
  type SettingsSection,
} from '../../model/records'
import { mintEdgeId } from '../../model/references'
import { routes } from '../../model/routes'
import { ComponentName } from '../../ui/ComponentName'
import { ContentPreview } from '../../ui/ContentPreview'
import { MediaPicker } from '../../ui/MediaPicker'
import { Loading, QueryError } from '../../ui/QueryState'
import { ReferencePicker } from '../../ui/ReferencePicker'
import { SitePreview } from '../../ui/SitePreview'
import { UndoButton } from '../../ui/UndoButton'
import { Picture } from './ContentRecords'

type Send = (id: string, intent: string, payload: Record<string, unknown>) => Promise<unknown>

/**
 * One record as its owner edits it: an album, its words, its photos and
 * its cover, on one screen.
 *
 * What the record owns is edited **in place**, with the editor its own
 * type has, so an album's photos are added here and not by finding its
 * Gallery in a library. A single relation that can hold one of those
 * owned things is offered as a choice among them: an album's cover is
 * one of its photos, picked by clicking it. A type that owns nothing has
 * its single relations chosen from the tenant's own instead: a service's
 * picture, from the library.
 *
 * **A record the site makes a page of has the page's Content and Preview
 * faces.** A pest or an album is a page to its owner, and the page editor
 * is where they learnt to check one; a testimonial, shown only inside
 * other pages, has nothing of its own to preview and keeps one face. The
 * site's generated route decides which, never the type's name.
 *
 * **Such a record has the page's Settings face as well.** What the page
 * says is on Content; its address, its menu name and what search engines
 * show are on Settings, where the page editor keeps them. Which fields
 * are which is read from how the site binds the record, through the
 * route and the pattern that lays the page out: see `settingsFields`.
 */
export function ContentRecord({ face = 'content' }: { face?: 'content' | 'settings' | 'preview' }) {
  const { tenant = '', site = '', type = '', id = '' } = useParams()
  const navigate = useNavigate()
  const manifest = useGetManifestQuery({ tenant })
  const record = useGetAuthoringComponentQuery({ tenant, id })
  const website = useGetAuthoringComponentQuery({ tenant, id: site })
  const declared = typeOf(manifest.data, type)
  const collectionId = declared?.collection ?? ''
  const collection = useGetAuthoringComponentQuery({ tenant, id: collectionId }, { skip: !collectionId })
  const { send, isLoading: sending, error: sendError } = useIntent(tenant)
  const [deleting, setDeleting] = useState(false)
  const address = recordAddress(website.data, collectionId)
  const pattern = useGetAuthoringComponentQuery({ tenant, id: address?.layout ?? '' }, { skip: !address?.layout })

  if (manifest.isLoading || record.isLoading || pattern.isLoading) return <Loading label="Reading…" />
  if (record.error) return <QueryError error={record.error} title="Could not read this" />
  if (!record.data) return null

  const document = record.data
  const prose = proseOf(declared, type)
  const name = nameOrId(document, manifest.data)
  const { key } = keyAndLabel(manifest.data, type)
  const fields = (document.fields ?? {}) as Record<string, unknown>
  const path = recordPath(address, key ? (fields[key] as string | undefined) : undefined)
  const Editor = editorFor(type, manifest.data)
  const owned = ownedReferences(manifest.data, type)
  const sendHere = (intent: string, payload: Record<string, unknown>) => send(id, intent, payload)
  const layout = pattern.data ? declarationOf((pattern.data.fields ?? {}) as Record<string, unknown>) : undefined
  const placed = settingsFields(manifest.data, type, address, layout)
  const settings = inFieldOrder(manifest.data, type, formableIntents(manifest.data, type, fields)).flatMap((intent) => {
    const section = intentSection(intent, placed)
    return section ? [{ intent, section }] : []
  })
  const omit = new Set(settings.map(({ intent }) => intent.name))

  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        <Breadcrumbs>
          <MuiLink component={RouterLink} to={routes.site(tenant, site)}>
            {website.data?.label ?? 'Site'}
          </MuiLink>
          <MuiLink component={RouterLink} to={routes.records(tenant, site, type)}>
            {prose.plural}
          </MuiLink>
          <Typography color="text.primary">{name}</Typography>
        </Breadcrumbs>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="h1">{name}</Typography>
          {path && address?.host && (
            <Button
              size="small"
              endIcon={<OpenInNewIcon fontSize="small" />}
              href={`https://${address.host}${path}`}
              target="_blank"
              rel="noreferrer"
            >
              {path}
            </Button>
          )}
        </Stack>
        <Stack direction="row" spacing={1}>
          <UndoButton tenant={tenant} document={document} manifest={manifest.data} disabled={sending} />
        </Stack>
        {address && (
          <Tabs value={face} sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tab label="Content" value="content" component={RouterLink} to={routes.record(tenant, site, type, id)} />
            <Tab label="Settings" value="settings" component={RouterLink} to={routes.recordSettings(tenant, site, type, id)} />
            <Tab label="Preview" value="preview" component={RouterLink} to={routes.recordPreview(tenant, site, type, id)} />
          </Tabs>
        )}
      </Stack>

      {document.tombstoned && <Alert severity="warning">This {prose.title.toLowerCase()} is deleted, and cannot be brought back.</Alert>}
      {sendError && <Alert severity="error">{problemMessage(sendError, 'That change was not made.')}</Alert>}

      {face === 'settings' ? (
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <RecordSettings
            settings={settings}
            order={sectionOrder(manifest.data, layout)}
            address={address}
            path={path}
            fields={fields}
            typeName={type}
            references={document.references ?? []}
            busy={sending}
            send={sendHere}
          />
        </Paper>
      ) : face === 'preview' ? (
        // The page face's two answers, in its order: what the page says
        // now, unpublished edits included, and what the live site shows.
        website.isLoading ? (
          <Loading label="Reading the site…" />
        ) : address ? (
          <Stack spacing={2}>
            <ContentPreview tenant={tenant} manifest={manifest.data} document={document} layout={address.layout} />
            <SitePreview tenant={tenant} site={site} host={address.host} path={path} size="screen" />
          </Stack>
        ) : (
          <Alert severity="info">{prose.plural} have no page of their own on this site, so there is nothing to preview.</Alert>
        )
      ) : (
        <>
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Editor
              tenant={tenant}
              commandId={id}
              commandPath={[]}
              fields={fields}
              children={document.children}
              references={document.references ?? []}
              manifest={manifest.data}
              typeName={type}
              register="content"
              omit={omit}
              busy={sending}
              send={sendHere}
            />
          </Paper>

          {owned.map((reference) => (
            <Owned
              key={reference.name}
              tenant={tenant}
              manifest={manifest.data}
              declares={reference}
              held={heldUnder(document.references, reference.name)}
              busy={sending}
              send={send}
            />
          ))}

          <Choices tenant={tenant} manifest={manifest.data} record={document} busy={sending} send={send} />

          {libraryChoices(manifest.data, type).map((reference) => (
            <FromLibrary
              key={reference.name}
              tenant={tenant}
              manifest={manifest.data}
              record={document}
              declares={reference}
              busy={sending}
              send={send}
            />
          ))}

          {!document.tombstoned && (
            <Box>
              <Button color="error" startIcon={<DeleteOutlineIcon />} onClick={() => setDeleting(true)} disabled={sending}>
                Delete this {prose.title.toLowerCase()}
              </Button>
            </Box>
          )}
        </>
      )}

      <DeleteRecordDialog
        tenant={tenant}
        open={deleting}
        record={document}
        title={prose.title.toLowerCase()}
        owned={owned.flatMap((reference) => heldUnder(document.references, reference.name) ?? [])}
        collection={collection.data}
        send={send}
        onClose={() => setDeleting(false)}
        onDeleted={() => {
          setDeleting(false)
          navigate(routes.records(tenant, site, type))
        }}
      />
    </Stack>
  )
}

/**
 * What a page editor's Settings face holds, for a record: the intents
 * whose every field the site uses as the page's settings, grouped as the
 * page editor groups them.
 */
function RecordSettings({
  settings,
  order,
  address,
  path,
  fields,
  typeName,
  references,
  busy,
  send,
}: {
  settings: { intent: ManifestIntent; section: SettingsSection }[]
  order: SettingsSection[]
  address: RecordAddress | undefined
  path: string | undefined
  fields: Record<string, unknown>
  typeName: string
  references: Reference[]
  busy: boolean
  send: (intent: string, payload: Record<string, unknown>) => Promise<unknown>
}) {
  const sections = order.filter((section) => settings.some((setting) => setting.section === section))
  // A section the root type does not order, which a stale pattern could
  // bind; drawn last rather than lost.
  for (const { section } of settings) if (!sections.includes(section)) sections.push(section)

  if (sections.length === 0) {
    return <Typography color="text.secondary">There is nothing to set here.</Typography>
  }
  return (
    <Stack spacing={4}>
      {sections.map((section) => (
        <Section key={section} title={sectionTitle(section)}>
          {section === ADDRESS_SECTION && (
            <Typography variant="body2" color="text.secondary">
              {path ? `Visitors reach this page at ${path}.` : `Its page will be at ${address?.pattern ?? 'its address'}.`}
            </Typography>
          )}
          {settings
            .filter((setting) => setting.section === section)
            .map(({ intent }) => (
              <IntentForm
                key={intent.name}
                intent={intent}
                fields={fields}
                heading={false}
                holder={typeName}
                references={references}
                showName={false}
                busy={busy}
                send={send}
              />
            ))}
        </Section>
      ))}
    </Stack>
  )
}

/**
 * The words a settings section is headed with: the page editor's own.
 *
 * Interim, and the one place this screen names a Page field: the Page's
 * schema carries no prose for `seo`, so "Search engines" is repeated from
 * `PageSettings` until it does. Anything else is the field's own name.
 */
function sectionTitle(section: SettingsSection): string {
  if (section === ADDRESS_SECTION) return 'Address and menus'
  if (section === 'seo') return 'Search engines'
  return humanise(section)
}

/** A run of controls that are one subject, with that subject named, as `PageSettings` draws one. */
function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Stack spacing={2}>
      <Typography variant="overline" color="text.secondary">
        {title}
      </Typography>
      {children}
    </Stack>
  )
}

/** One owned target, edited in place with its own type's editor. */
function Owned({
  tenant,
  manifest,
  declares,
  held,
  busy,
  send,
}: {
  tenant: string
  manifest: Manifest | undefined
  declares: ManifestReference
  held: Reference | undefined
  busy: boolean
  send: Send
}) {
  const target = useGetAuthoringComponentQuery({ tenant, id: held?.id ?? '' }, { skip: !held })
  const title = humanise(declares.name)
  if (!held) {
    return (
      <Alert severity="warning">
        This has no {title.toLowerCase()} yet. It was created before one came with it. Ask your agency to add one.
      </Alert>
    )
  }
  if (target.isLoading) return <Loading label={`Reading its ${title.toLowerCase()}…`} />
  if (!target.data) return null
  const Editor = editorFor(target.data.type, manifest)
  return (
    <Stack spacing={1.5}>
      <Typography variant="h2">{title}</Typography>
      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Editor
          tenant={tenant}
          commandId={held.id}
          commandPath={[]}
          fields={(target.data.fields ?? {}) as Record<string, unknown>}
          children={target.data.children}
          references={target.data.references ?? []}
          manifest={manifest}
          typeName={target.data.type}
          register="content"
          busy={busy}
          send={(intent, payload) => send(held.id, intent, payload)}
        />
      </Paper>
    </Stack>
  )
}

/**
 * The single relations an owner fills from the record's own things: a
 * cover from the album's photos. Offered only where the owned targets hold
 * something the relation accepts.
 */
function Choices({
  tenant,
  manifest,
  record,
  busy,
  send,
}: {
  tenant: string
  manifest: Manifest | undefined
  record: Document
  busy: boolean
  send: Send
}) {
  const owned = ownedReferences(manifest, record.type).flatMap((reference) => heldUnder(record.references, reference.name) ?? [])
  // One owned target is read here, which is the case there is: an album
  // owns one Gallery. More would each need a read and a merge.
  const source = useGetAuthoringComponentQuery({ tenant, id: owned[0]?.id ?? '' }, { skip: owned.length === 0 })
  // A Gallery arranges its photos by `items`, which is what `members` reads.
  const offered = members(source.data)

  return (
    <>
      {ownedChoices(manifest, record.type).map((reference) => {
        const candidates = offered.filter((child) => reference.accepts.includes(child.type))
        if (owned.length === 0) return null
        const held = heldUnder(record.references, reference.name)
        const choose = (target: string) => pointAt(tenant, record, reference, target, send)
        return (
          <Stack key={reference.name} spacing={1.5}>
            <Typography variant="h2">{humanise(reference.name)}</Typography>
            {candidates.length === 0 ? (
              <Typography color="text.secondary">
                Add some first, and then choose one of them as the {humanise(reference.name).toLowerCase()}.
              </Typography>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary">
                  Choose one of them as its {humanise(reference.name).toLowerCase()}.
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {candidates.map((child, index) => (
                    <ButtonBase
                      key={child.linkId}
                      disabled={busy}
                      onClick={() => choose(child.id ?? '').catch(shown)}
                      aria-pressed={held?.id === child.id}
                      aria-label={`Use photo ${index + 1} as the ${humanise(reference.name).toLowerCase()}`}
                      sx={{
                        borderRadius: 1,
                        outline: held?.id === child.id ? 3 : 0,
                        outlineColor: 'primary.main',
                        outlineOffset: 2,
                      }}
                    >
                      <Picture tenant={tenant} id={child.id} size={96} />
                    </ButtonBase>
                  ))}
                </Stack>
              </>
            )}
          </Stack>
        )
      })}
    </>
  )
}

/**
 * Point a record's single relation at a target: re-pointed where it holds
 * one, so the record is never without it between two commands, and added
 * where it does not.
 */
async function pointAt(tenant: string, record: Document, reference: ManifestReference, target: string, send: Send) {
  const held = heldUnder(record.references, reference.name)
  if (held?.id === target) return
  if (held) {
    await send(record.id, 'component.reference_retargeted', { edgeId: held.edgeId, id: payloadId(tenant, target) })
    return
  }
  const taken = new Set((record.references ?? []).map((edge) => edge.edgeId))
  await send(record.id, 'component.reference_added', {
    edgeId: mintEdgeId(reference.name, taken),
    name: reference.name,
    id: payloadId(tenant, target),
  })
}

/**
 * A single relation chosen from the tenant's own things: a service's
 * picture. Where the relation accepts only something holding a file it
 * opens the library, which can take a new upload as well; anything else
 * opens the general picker.
 */
function FromLibrary({
  tenant,
  manifest,
  record,
  declares,
  busy,
  send,
}: {
  tenant: string
  manifest: Manifest | undefined
  record: Document
  declares: ManifestReference
  busy: boolean
  send: Send
}) {
  const [picking, setPicking] = useState(false)
  const held = heldUnder(record.references, declares.name)
  const file = fileTarget(manifest, declares)
  const title = humanise(declares.name)
  // What is being chosen, as the picker calls it: "an image or file".
  const noun = file ? typeProse(manifest, file).title.toLowerCase() : title.toLowerCase()
  const choose = (target: string) => {
    setPicking(false)
    pointAt(tenant, record, declares, target, send).catch(shown)
  }

  return (
    <Stack spacing={1.5}>
      <Typography variant="h2">{title}</Typography>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        {held ? (
          file ? (
            <Picture tenant={tenant} id={held.id} size={96} />
          ) : (
            <ComponentName tenant={tenant} id={held.id} manifest={manifest} />
          )
        ) : (
          <Typography color="text.secondary">None chosen yet.</Typography>
        )}
        <Button variant="outlined" disabled={busy || record.tombstoned} onClick={() => setPicking(true)}>
          {held ? `Choose a different ${noun}` : `Choose ${aOrAn(noun)}`}
        </Button>
        {held && (
          <Button
            color="inherit"
            disabled={busy || record.tombstoned}
            onClick={() => send(record.id, 'component.reference_removed', { edgeId: held.edgeId }).catch(shown)}
          >
            Take it off
          </Button>
        )}
      </Stack>
      {file ? (
        <MediaPicker
          tenant={tenant}
          open={picking}
          typeName={file}
          manifest={manifest}
          chosen={held?.id}
          busy={busy}
          onClose={() => setPicking(false)}
          onChoose={(component) => choose(component.id)}
        />
      ) : (
        <ReferencePicker
          tenant={tenant}
          declares={picking ? declares : undefined}
          open={picking}
          onClose={() => setPicking(false)}
          onChoose={(target) => choose(target.id)}
          busy={busy}
        />
      )}
    </Stack>
  )
}

/**
 * Deleting a record takes it out of its list as well, and offers to take
 * what it owns with it when nothing else holds that.
 */
function DeleteRecordDialog({
  tenant,
  open,
  record,
  title,
  owned,
  collection,
  send,
  onClose,
  onDeleted,
}: {
  tenant: string
  open: boolean
  record: Document
  title: string
  owned: Reference[]
  collection: Document | undefined
  send: Send
  onClose: () => void
  onDeleted: () => void
}) {
  const [withOwned, setWithOwned] = useState(true)
  const [error, setError] = useState<unknown>()
  const [working, setWorking] = useState(false)
  const target = owned[0]
  const usages = useListUsagesQuery({ tenant, id: target?.id ?? '' }, { skip: !open || !target })
  // Held only by this record, so nothing else loses it.
  const onlyHere = (usages.data?.usages ?? []).every((usage) => usage.id === record.id)
  const entry = collection?.children.find((child) => child.id === record.id)

  const run = async () => {
    setWorking(true)
    setError(undefined)
    try {
      if (entry && collection) await send(collection.id, 'collection.item_removed', { linkId: entry.linkId })
      await send(record.id, 'component.tombstoned', {})
      if (withOwned && onlyHere) {
        for (const reference of owned) await send(reference.id, 'component.tombstoned', {})
      }
      onDeleted()
    } catch (caught) {
      setError(caught)
    } finally {
      setWorking(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Delete this {title}?</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography>
            It is removed from the list, and from the site when you next publish. This cannot be undone.
          </Typography>
          {target && onlyHere && (
            <FormControlLabel
              control={<Checkbox checked={withOwned} onChange={(event) => setWithOwned(event.target.checked)} />}
              label={`Delete its ${humanise(target.name).toLowerCase()} too`}
            />
          )}
          {target && !onlyHere && (
            <Typography variant="body2" color="text.secondary">
              Its {humanise(target.name).toLowerCase()} is used elsewhere as well, so it is kept.
            </Typography>
          )}
          {error !== undefined && <Alert severity="error">{problemMessage(error, 'It was not deleted.')}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={working}>
          Cancel
        </Button>
        <Button color="error" variant="contained" onClick={() => run().catch(shown)} disabled={working || usages.isFetching}>
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  )
}
